/**
 * Các khoản THƯỞNG — chính là dòng "Mốc thưởng" của phiếu, mở ra.
 *
 * Máy lương gộp sẵn vào `milestoneBonus`: mốc theo khách của phòng, thưởng mốc
 * bán thêm (upsell.go) và CẢ gói thưởng động lực (motivation.go — chuỗi ngày,
 * quán quân, mốc tháng). Bản cũ cộng `milestoneBonus` với `motivationBonus` là
 * in gói động lực hai lần. Ở đây tổng của mục = `milestoneBonus`, các dòng con
 * lấy từ `payslipLines` — cùng nguồn với phiếu, không tự tách lại.
 */

import type { PayrollRecord } from "../../lib";
import { vnd } from "../../constants";
import { payslipLines } from "../../payslip-lines";
import { INSTALLMENT_KINDS } from "../../trainingFee";
import { AmountRow, AmountTable, DetailSection } from "./primitives";

const pct = (r: number) => `${Number((r * 100).toFixed(2))}%`;

/** testid cũ của từng dòng — giữ để các bài kiểm và ảnh chụp không đổi tên. */
const TEST_ID: Record<string, string> = {
  clientMilestone: "bonus-client-milestone",
  upsellMilestone: "bonus-upsell-milestone",
  streak: "bonus-streak",
  champion: "bonus-champion",
  motivationTiers: "bonus-motivation-tiers",
};

export function BonusDetail({ record }: { record: PayrollRecord }) {
  const total = record.milestoneBonus ?? 0;
  if (total === 0) return null;
  const rows = payslipLines(record).within.milestoneBonus ?? [];

  return (
    <DetailSection testId="detail-bonus" icon="🏆" title="Thưởng" total={total}>
      <AmountTable>
        {rows.map((l) => (
          <AmountRow
            key={l.key}
            testId={TEST_ID[l.key]}
            label={l.label}
            hint={
              l.key === "upsellMilestone" && record.upsellMilestoneRate
                ? `${pct(record.upsellMilestoneRate)} × doanh số bán thêm đã chín`
                : l.note
            }
            value={l.amount}
          />
        ))}
      </AmountTable>
    </DetailSection>
  );
}

/**
 * Cộng / trừ ngoài công thức: khoản tự do trưởng phòng nhập, điều chỉnh của
 * giám đốc đã được duyệt, bảo hiểm và phí công đoàn.
 *
 * Đây là mục hay bị thắc mắc nhất vì không sinh ra từ việc làm hằng ngày —
 * nên mỗi dòng đều mang theo lời giải thích, và lý do điều chỉnh hiện nguyên
 * văn chứ không tóm tắt.
 */
export function OtherAmountsDetail({ record }: { record: PayrollRecord }) {
  const additions = record.customAdditionItems ?? [];
  // Dòng phí đào tạo Master Class (QĐ 0298) đã có dòng riêng ở mục Tổng hợp —
  // liệt kê ở đây nữa là cùng một khoản trừ đứng hai nơi.
  // Bồi thường khiếu nại (QĐ 0384) đi cùng bước đó, cũng có dòng riêng.
  const deductions = (record.customDeductionItems ?? []).filter(
    (item) => !INSTALLMENT_KINDS.includes(item.Kind ?? ""),
  );
  const bonus = record.bonusAmount ?? 0;
  const deduction = record.deductionAmount ?? 0;
  const insurance = record.insurance ?? 0;
  const unionFee = record.unionFee ?? 0;

  const hasAny =
    additions.length > 0 ||
    deductions.length > 0 ||
    bonus !== 0 ||
    deduction !== 0 ||
    insurance !== 0 ||
    unionFee !== 0;
  if (!hasAny) return null;

  // ⚠️ Khoản tự do dùng khoá VIẾT HOA (`Amount` / `Label`) — struct CustomItem
  // bên Go không gắn thẻ json nên tên trường đi thẳng ra API như vậy. Đây là
  // hợp đồng đang chạy, không phải lỗi gõ; đổi thành chữ thường là gãy.
  const total =
    additions.reduce((s, i) => s + (i.Amount ?? 0), 0) -
    deductions.reduce((s, i) => s + (i.Amount ?? 0), 0) +
    bonus -
    deduction +
    insurance -
    unionFee;

  return (
    <DetailSection
      testId="detail-other"
      icon="📌"
      title="Cộng / trừ khác"
      total={total}
    >
      <AmountTable>
        {additions.map((item, i) => (
          <AmountRow
            key={`add-${i}`}
            label={item.Label || "Khoản cộng"}
            value={`+ ${vnd(item.Amount ?? 0)}`}
          />
        ))}
        {deductions.map((item, i) => (
          <AmountRow
            key={`ded-${i}`}
            label={item.Label || "Khoản trừ"}
            value={`− ${vnd(item.Amount ?? 0)}`}
          />
        ))}
        {bonus !== 0 ? (
          <AmountRow
            testId="other-bonus"
            label="Thưởng thêm (giám đốc duyệt)"
            value={`+ ${vnd(bonus)}`}
          />
        ) : null}
        {deduction !== 0 ? (
          <AmountRow
            testId="other-deduction"
            label="Khấu trừ (giám đốc duyệt)"
            value={`− ${vnd(deduction)}`}
          />
        ) : null}
        {insurance !== 0 ? (
          <AmountRow
            label="Phụ cấp bảo hiểm"
            value={`+ ${vnd(insurance)}`}
          />
        ) : null}
        {unionFee !== 0 ? (
          <AmountRow label="Phí công đoàn" value={`− ${vnd(unionFee)}`} />
        ) : null}
      </AmountTable>

      {record.adjustmentNote ? (
        <p className="mt-2 text-xs italic text-muted-foreground">
          Lý do điều chỉnh: {record.adjustmentNote}
        </p>
      ) : null}
    </DetailSection>
  );
}
